// Shared branding mapping for the two public booking pages (PublicBooking.tsx, ManageBooking.tsx).
// NOT a component. Both pages MUST import brandingVars/brandingLogo from here — the mapping must
// never be copy-pasted into two components (that is how --pff-page-bg style drift starts, see
// booking-form-branding.md's CSS Notes). Mirrors document_form_style's --pff-* technique with a
// --bk-* prefix, plus one extra key (logo_url) that FormIntake's style contract doesn't have.
import type { CSSProperties } from 'react';

// The 11 allowlisted keys from booking_style.py's _ALLOWED_KEYS — all optional. All colours are
// '#rrggbb' hex strings validated server-side; logo_url is a root-relative asset path or an
// absolute https URL, also validated server-side. Never raw CSS.
export interface BookingBranding {
  header_color?: string;
  text_color?: string;
  background_color?: string;
  page_background?: string;
  input_background?: string;
  input_text_color?: string;
  border_color?: string;
  button_color?: string;
  button_text_color?: string;
  font_family?: string;
  logo_url?: string;
}

/**
 * Maps a booking style dict onto --bk-* CSS custom properties for use as the `style` prop on the
 * `.bk-page` wrapper. Every property is set only when present on `b` — when absent, the CSS rules
 * in Booking.css fall back to the pre-existing token (var(--bk-header, var(--text)), etc.), so an
 * unbranded page (b == null/undefined/{}) renders byte-identical to before this feature.
 */
export const brandingVars = (b?: BookingBranding | null): CSSProperties => {
  if (!b) return {};
  const vars: Record<string, string> = {};
  if (b.page_background) vars['--bk-page-bg'] = b.page_background;
  if (b.font_family) vars['--bk-font'] = `'${b.font_family}', -apple-system, sans-serif`;
  if (b.background_color) vars['--bk-bg'] = b.background_color;
  if (b.border_color) vars['--bk-border'] = b.border_color;
  if (b.header_color) vars['--bk-header'] = b.header_color;
  if (b.text_color) vars['--bk-text'] = b.text_color;
  if (b.input_background) vars['--bk-input-bg'] = b.input_background;
  if (b.input_text_color) vars['--bk-input-text'] = b.input_text_color;
  if (b.button_color) vars['--bk-btn-bg'] = b.button_color;
  if (b.button_text_color) vars['--bk-btn-text'] = b.button_text_color;
  return vars as CSSProperties;
};

/** The validated logo_url to render, or undefined (no logo / no branding). */
export const brandingLogo = (b?: BookingBranding | null): string | undefined => b?.logo_url || undefined;
